/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 에디터 클래스를 정의한다.
 *
 * @file /src/rootin/core/Editor.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 9.
 */
import { Tools, type Tool, type ToolClass } from '@rootin/base/Tools';
import { Blocks, type Block, type BlockClass } from '@rootin/base/Blocks';
import { Root } from '@rootin/blocks/root/Root';
import { Container } from '@rootin/elements/container/Container';
import { Focus } from '@rootin/selection/Focus';
import { History } from '@rootin/core/History';
import { Events } from '@rootin/core/Events';
import { Caret } from '@rootin/selection/Caret';
import { Selection } from '@rootin/selection/Selection';
import { Mark } from '@rootin/selection/Mark';
import { Menu } from '@rootin/core/Menu';
import { Html } from '@utils/Html';
import { Dom } from '@utils/Dom';

export interface Listeners {}

export interface Properties {
    /**
     * 파일업로드 URL
     */
    url?: string;

    /**
     * 사용할 툴 목록
     */
    tools?: (string | { [type: string]: { [key: string]: any } } | ToolClass)[];

    /**
     * 사용할 블록 목록
     */
    blocks?: (string | { [type: string]: { [key: string]: any } } | BlockClass)[];

    /**
     * 블록메뉴를 사용할 지 여부
     */
    blockMenu?: boolean;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners;
}

export class Editor {
    /**
     * 파일업로드 URL
     */
    url: string;

    /**
     * 툴 목록
     */
    tools: Tool[] = [];

    /**
     * 블록타입 클래스 레지스트리
     */
    blockClasses: Map<string, BlockClass> = new Map();

    /**
     * 블록타입별 설정값 레지스트리
     */
    blockConfig: Map<string, { [key: string]: any }> = new Map();

    /**
     * 툴타입별 설정값 레지스트리
     */
    toolConfig: Map<string, { [key: string]: any }> = new Map();

    /**
     * 블록메뉴를 사용할 지 여부
     */
    blockMenu: boolean;

    /**
     * 루트블록
     */
    root: Root;

    /**
     * 포커스 위치 클래스
     */
    focus: Focus;

    /**
     * 캐럿 이동 클래스
     */
    caret: Caret;

    /**
     * 선택영역 클래스
     */
    selection: Selection;

    /**
     * 인라인 서식(마크: 굵게/기울임 등) 클래스
     */
    mark: Mark;

    /**
     * 히스토리 클래스
     */
    history: History;

    /**
     * DOM 이벤트 클래스
     */
    events: Events;

    /**
     * 에디터 메뉴
     */
    menu: Menu | null = null;

    /**
     * 컨테이너 DOM
     */
    $container: Dom = null;

    /**
     * 블록바 DOM
     */
    $blocks: Dom = null;

    /**
     * 툴바 DOM
     */
    $tools: Dom = null;

    /**
     * 블록상태변경이 확정되지 않은 블록목록
     */
    #pendingBlocks: Set<Block> = new Set();

    /**
     * 블록상태변경을 동기화중인지 여부
     */
    #syncScheduled: boolean = false;

    /**
     * JSON 모드 textarea 높이 재계산이 예약되어있는지 여부 (rAF 중복 예약 방지)
     */
    #jsonHeightSyncScheduled: boolean = false;

    /**
     * JSON 모드에서 footer 크기변경을 감시하는 옵저버
     */
    #jsonHeightResizeObserver: ResizeObserver | null = null;

    /**
     * JSON 모드에서 뷰포트 높이변화(window resize)를 감시하는 핸들러
     */
    #jsonHeightResizeHandler: (() => void) | null = null;

    /**
     * JSON 모드에서 스크롤을 감시하는 핸들러 (스크롤은 버블링되지 않으므로 capture 로 등록)
     */
    #jsonHeightScrollHandler: (() => void) | null = null;

    /**
     * 에디터 클래스를 정의한다.
     *
     * @param $textarea - JSON 출력 대상 textarea
     * @param properties - 초기설정
     */
    constructor(
        public $textarea: Dom<HTMLTextAreaElement>,
        public properties: Properties
    ) {
        this.url = properties?.url ?? '/';

        for (const entry of properties.tools ?? ['bold']) {
            let ToolClass: ToolClass;
            let options: { [key: string]: any } | undefined;

            if (typeof entry === 'string') {
                ToolClass = Tools.get(entry);
            } else if (typeof entry === 'function') {
                ToolClass = entry;
            } else {
                const type = Object.keys(entry)[0];
                ToolClass = Tools.get(type);
                options = entry[type];
            }

            if (options !== undefined) {
                this.toolConfig.set(ToolClass.type, options);
            }
            this.addTool(new ToolClass());
        }

        for (const entry of properties.blocks ?? [
            'paragraph',
            'heading',
            'quote',
            'callout',
            'images',
            'list',
            'table',
            'talk',
            'divider',
        ]) {
            let BlockClass: BlockClass;
            let options: { [key: string]: any } | undefined;

            if (typeof entry === 'string') {
                BlockClass = Blocks.get(entry);
            } else if (typeof entry === 'function') {
                BlockClass = entry;
            } else {
                const type = Object.keys(entry)[0];
                BlockClass = Blocks.get(type);
                options = entry[type];
            }

            this.blockClasses.set(BlockClass.type, BlockClass);
            if (options !== undefined) {
                this.blockConfig.set(BlockClass.type, options);
            }
            this.$getBlocks().append(BlockClass.init(this));
        }

        this.root = new Root(this, [...this.blockClasses.keys()]);
        this.blockMenu = properties.blockMenu ?? true;
        this.history = new History(this);
        this.focus = new Focus(this);
        this.caret = new Caret(this);
        this.selection = new Selection(this);
        this.mark = new Mark(this);
        this.events = new Events(this);
    }

    /**
     * 컨테이너 DOM 을 가져온다.
     *
     * @returns 컨테이너 DOM
     */
    $getContainer(): Dom {
        if (this.$container === null) {
            this.$container = Html.create('div', {
                'data-role': 'rootin',
                'data-mode': 'editor',
                'data-block-menu': this.blockMenu == true ? 'true' : 'false',
            });

            const $bar = Html.create('div', { 'data-role': 'bar' });
            $bar.append(this.$getBlocks());
            $bar.append(this.$getTools());
            this.$container.append($bar);
            this.$container.append(this.root.$getBlock());
        }

        return this.$container;
    }

    /**
     * 블록바 DOM 을 가져온다.
     *
     * @returns 블록바 DOM
     */
    $getBlocks(): Dom {
        if (this.$blocks === null) {
            this.$blocks = Html.create('div', { 'data-role': 'blocks' });
        }

        return this.$blocks;
    }

    /**
     * 툴바 DOM 을 가져온다.
     *
     * @returns 툴바 DOM
     */
    $getTools(): Dom {
        if (this.$tools === null) {
            this.$tools = Html.create('div', { 'data-role': 'tools' });
        }

        return this.$tools;
    }

    /**
     * 타입 문자열로 블록 클래스를 가져온다.
     *
     * @param type - 블록타입
     * @returns 해당 블록 클래스
     */
    getBlockClass(type: string): BlockClass | null {
        return this.blockClasses.get(type) ?? null;
    }

    /**
     * 툴을 등록한다.
     * 직전에 등록된 툴과 그룹이 다르면 그 사이에 구분자를 끼워넣는다.
     *
     * @param tool - 등록할 툴 인스턴스
     */
    addTool(tool: Tool): void {
        tool.editor = this;

        const previous = this.tools.at(-1);
        if (
            previous !== undefined &&
            previous instanceof Tools.Separator === false &&
            (previous.constructor as ToolClass).group !== (tool.constructor as ToolClass).group
        ) {
            const separator = new Tools.Separator();
            separator.editor = this;
            this.tools.push(separator);
        }

        this.tools.push(tool);
    }

    /**
     * 블록 또는 툴의 설정값을 가져온다.
     *
     * @param target - 조회할 블록 클래스 또는 툴 인스턴스
     * @param key - 조회할 설정 키
     * @returns 설정값
     */
    getConfig(target: BlockClass | Tool, key: string): any {
        if (typeof target === 'function') {
            return this.blockConfig.get(target.type)?.[key];
        }

        return this.toolConfig.get((target.constructor as ToolClass).type)?.[key];
    }

    /**
     * 현재 포커스된 위치에 따라 툴바 표시/활성여부를 갱신한다.
     * 구분자는 스스로 표시여부를 판단하지 않으므로, 실제 툴을 모두 갱신한 뒤 양옆에
     * 보이는 실제 툴이 있는 구분자만 두번째 패스에서 보여준다(맨 앞/뒤 또는 구분자끼리
     * 연달아 남는 것을 막기 위함 — `tools/separator/Separator.ts` 파일 헤더 참고).
     */
    refreshTools(): void {
        const block = this.focus.getBlock();
        const element = this.focus.getElement();

        for (const tool of this.tools) {
            if (tool instanceof Tools.Separator) {
                continue;
            }
            tool.refresh(block, element);
        }

        for (let i = 0; i < this.tools.length; i++) {
            const tool = this.tools[i];
            if (tool instanceof Tools.Separator === false) {
                continue;
            }

            let before = false;
            for (let j = i - 1; j >= 0; j--) {
                if (this.tools[j] instanceof Tools.Separator) {
                    break;
                }
                if (this.tools[j].$getContainer().hasClass('hidden') === false) {
                    before = true;
                    break;
                }
            }

            let after = false;
            for (let j = i + 1; j < this.tools.length; j++) {
                if (this.tools[j] instanceof Tools.Separator) {
                    break;
                }
                if (this.tools[j].$getContainer().hasClass('hidden') === false) {
                    after = true;
                    break;
                }
            }

            (tool as InstanceType<typeof Tools.Separator>).setVisible(before && after);
        }
    }

    /**
     * 현재 포커스된 위치를 기준으로 블록바 표시여부를 갱신한다.
     */
    refreshBlocks(): void {
        const blocks = this.#resolveInsertContainer().blocks;

        for (const $button of Html.all('button[data-type]', this.$getBlocks()).getList()) {
            $button.setDisabled(blocks.includes($button.getAttr('data-type')) === false);
        }
    }

    /**
     * 블록을 현재 활성화된 컨테이너에 삽입한다.
     *
     * @param block - 삽입할 블록
     */
    async insertBlock(block: Block): Promise<void> {
        const container = this.#resolveInsertContainer();
        const before = this.#resolveInsertPosition(container);
        await container.insertBlock(block, before);
    }

    /**
     * 블록 DOM 의 DOM 연결여부 변경에 따라 블록상태를 동기화한다.
     * 한다.
     *
     * @param block - 연결되거나 연결해제된 Block
     */
    syncBlocks(block: Block): void {
        this.#pendingBlocks.add(block);

        if (this.#syncScheduled === true) {
            return;
        }

        this.#syncScheduled = true;
        requestAnimationFrame(() => this.#confirmDisconnected());
    }

    /**
     * 에디터 메뉴를 가져온다.
     *
     * @returns 새로 생성된 메뉴
     */
    getMenu(): Menu {
        throw new Error('구현 예정 — this.menu?.close(); this.menu = new Menu(this, {}); return this.menu;');
    }

    /**
     * 에디터값을 가져온다.
     *
     * @returns 에디터값
     */
    getValue(): { [key: string]: any }[] {
        return this.root.body.getValue();
    }

    /**
     * 에디터값을 설정한다.
     *
     * @param value - 에디터값
     */
    async setValue(value: { [key: string]: any }[]): Promise<void> {
        this.history.clear();
        await this.root.body.setValue(value);
        this.history.seedSnapshots();
        this.focus.setBlock(null);
    }

    /**
     * 에디터 내용을 HTML 으로 변환하여 가져온다.
     *
     * @returns 에디터 내용
     */
    async toHtml(): Promise<string> {
        throw new Error('구현 예정');
    }

    /**
     * 에디터를 렌더링한다.
     */
    async render(): Promise<void> {
        for (const tool of this.tools) {
            this.$getTools().append(tool.$getContainer());
            await tool.render();
        }

        await this.root.render();
        this.$textarea.getParent().insertAfter(this.$getContainer(), this.$textarea);

        const $footer = Html.create('div', { 'data-role': 'footer' });

        const $json = Html.create('div', { 'data-role': 'json' });
        $json.append(this.$textarea);
        $footer.append($json);

        const $tab = Html.create('div', { 'data-role': 'tab' });

        $tab.append(Html.create('button', { type: 'button', 'data-tab': 'editor' }, 'EDITOR'));
        $tab.append(Html.create('button', { type: 'button', 'data-tab': 'json' }, 'JSON'));
        $footer.append($tab);

        this.$textarea.on('input', () => {
            let value: { [key: string]: any }[];
            try {
                value = JSON.parse(this.$textarea.getValue());
            } catch (e) {
                return;
            }

            if (Array.isArray(value) === false) {
                return;
            }

            this.setValue(value);
        });

        Html.all('button', $footer).on('click', async ($button) => {
            const mode = $button.getAttr('data-tab');

            if (mode == this.$getContainer().getAttr('data-mode')) {
                return;
            }

            if (mode == 'editor') {
                let value: { [key: string]: any }[];
                try {
                    value = JSON.parse(this.$textarea.getValue());
                } catch (e) {
                    return;
                }

                if (Array.isArray(value) === false) {
                    return;
                }

                await this.setValue(value);
                this.refreshTools();
                this.refreshBlocks();
                this.#stopJsonHeightSync();
            } else {
                this.$textarea.setValue(JSON.stringify(this.getValue(), null, 4), false);
                this.#startJsonHeightSync();
            }

            this.$getContainer().setAttr('data-mode', mode);
        });

        this.$getContainer().append($footer);

        this.events.listen();
        this.refreshTools();
    }

    /**
     * JSON 패널(`div[data-role='json']`)의 높이를 다시 계산한다.
     *
     * footer 는 `position: sticky; bottom: 0;` 이라 자신의 바닥(bottom)은 스크롤과 무관하게
     * 항상 뷰포트 하단(또는 문서 원래 위치)에 고정돼있다 — 그 바닥에서 tab 높이를 뺀 만큼만
     * json 이 차지하면, footer 의 위쪽 끝이 정확히 툴바 바닥에 맞물린다.
     */
    #syncJsonHeight(): void {
        const $bar = Html.get("div[data-role='bar']", this.$getContainer());
        const $footer = Html.get("div[data-role='footer']", this.$getContainer());
        const $tab = Html.get("div[data-role='tab']", this.$getContainer());
        const $json = Html.get("div[data-role='json']", this.$getContainer());
        if ($bar === null || $footer === null || $tab === null || $json === null) {
            return;
        }

        const barBottom = $bar.getEl().getBoundingClientRect().bottom;
        const footerBottom = $footer.getEl().getBoundingClientRect().bottom;
        const tabHeight = $tab.getEl().getBoundingClientRect().height;

        // 소수점 그대로 지정하면, 이 높이변경이 곧바로 컨테이너 크기변경으로 이어져서(아래
        // ResizeObserver 참고) 매 프레임 미세하게 다른 값으로 무한히 재계산되는 문제가 있었다
        // — 정수로 반올림하고, 실제로 값이 달라졌을 때만 스타일을 새로 지정한다.
        const height = Math.max(0, Math.round(footerBottom - barBottom - tabHeight));
        if ($json.getEl().style.height !== height + 'px') {
            $json.setStyle('height', height + 'px');
        }
    }

    /**
     * `#syncJsonHeight()` 호출을 한 프레임에 하나로 묶는다(스크롤/리사이즈가 짧은 시간에
     * 여러 번 발생해도 계산은 한 번만).
     */
    #scheduleJsonHeightSync(): void {
        if (this.#jsonHeightSyncScheduled === true) {
            return;
        }

        this.#jsonHeightSyncScheduled = true;
        requestAnimationFrame(() => {
            this.#jsonHeightSyncScheduled = false;
            this.#syncJsonHeight();
        });
    }

    /**
     * JSON 모드에 들어갈 때, footer 크기변경·스크롤을 감시해 높이를 계속 맞춘다.
     */
    #startJsonHeightSync(): void {
        this.#syncJsonHeight();

        // 툴바 크기변경(너비 변화로 인한 줄바꿈 등)은 ResizeObserver 로 감시한다. 컨테이너
        // 전체를 감시하면 json 자신의 높이변경이 곧 컨테이너 크기변경이라 스스로를 다시
        // 트리거하는 무한루프가 생기므로, json/footer 와 무관한 툴바만 감시 대상으로 삼는다.
        const $bar = Html.get("div[data-role='bar']", this.$getContainer());
        if ($bar !== null) {
            this.#jsonHeightResizeObserver = new ResizeObserver(() => this.#scheduleJsonHeightSync());
            this.#jsonHeightResizeObserver.observe($bar.getEl());
        }

        // 뷰포트 높이만 바뀌는 경우(컨테이너 자신의 박스 높이는 내용에 따라 정해지므로
        // 그대로일 수 있다)는 ResizeObserver 가 못 잡으므로 window resize 를 따로 듣는다.
        this.#jsonHeightResizeHandler = () => this.#scheduleJsonHeightSync();
        window.addEventListener('resize', this.#jsonHeightResizeHandler);

        this.#jsonHeightScrollHandler = () => this.#scheduleJsonHeightSync();
        document.addEventListener('scroll', this.#jsonHeightScrollHandler, true);
    }

    /**
     * 에디터 모드로 돌아갈 때, JSON 높이 감시를 멈춘다.
     */
    #stopJsonHeightSync(): void {
        this.#jsonHeightResizeObserver?.disconnect();
        this.#jsonHeightResizeObserver = null;

        if (this.#jsonHeightResizeHandler !== null) {
            window.removeEventListener('resize', this.#jsonHeightResizeHandler);
            this.#jsonHeightResizeHandler = null;
        }

        if (this.#jsonHeightScrollHandler !== null) {
            document.removeEventListener('scroll', this.#jsonHeightScrollHandler, true);
            this.#jsonHeightScrollHandler = null;
        }
    }

    /**
     * 에디터 내용이 변경되었을 때 이벤트를 처리한다.
     */
    onChange(): void {
        const value = this.getValue();
        this.$textarea.setValue(JSON.stringify(value, null, 4), false);
    }

    /**
     * 블록 DOM 의 상태변경에 따라 히스토리를 기록하거나, 블록추가마커 위치를 동기화한다.
     */
    async #confirmDisconnected(): Promise<void> {
        const removed = [...this.#pendingBlocks].filter((block) => block.$getBlock().getEl().isConnected === false);

        const containers = new Set<Container>();
        for (const block of removed) {
            const container = this.#findOwningContainer(block.getId());
            if (container !== null) {
                containers.add(container);
            }
        }

        for (const container of containers) {
            this.#stripForeignMarkup(container);
        }

        if (this.events.composition.isComposing() === true) {
            requestAnimationFrame(() => this.#confirmDisconnected());
            return;
        }

        this.#syncScheduled = false;
        this.#pendingBlocks = new Set();

        if (removed.length > 0) {
            for (const container of containers) {
                await container.seedIfEmpty();
            }

            const start = this.history.stack.length;
            for (const container of containers) {
                container.onEdit();
            }

            const entries = this.history.stack.splice(start);
            if (entries.length > 1) {
                this.history.stack.push({ type: 'batch', entries });
            } else if (entries.length === 1) {
                this.history.stack.push(entries[0]);
            }
        }

        this.#syncAddMarkers();
    }

    /**
     * 블록이 변경되었을 경우, 네이티브 병합에 의해 생겨난 마크업테그를 제거한다.
     *
     * @param container - 정리할 컨테이너
     */
    #stripForeignMarkup(container: Container): void {
        const ALLOWED = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'DEL', 'BR', 'ROOTIN-MARKUP']);
        const BLOCK = new Set(['DIV', 'P', 'LI', 'UL', 'OL', 'BLOCKQUOTE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']);

        for (const $text of Html.all('rootin-element[data-type="text"]', container.$getElement()).getList()) {
            const foreign = Array.from($text.getEl().querySelectorAll('*')).filter(
                (el) => ALLOWED.has(el.tagName) === false
            );

            for (const el of foreign) {
                const needsBreak = BLOCK.has(el.tagName) === true && el.nextSibling !== null;
                el.replaceWith(...el.childNodes, ...(needsBreak ? [document.createElement('br')] : []));
            }
        }
    }

    /**
     * 블록추가마커 위치를 동기화한다.
     */
    #syncAddMarkers(): void {
        for (const $container of Html.all('[data-type="container"]', this.$getContainer()).getList()) {
            const el = $container.getEl();
            const container = $container.getData('element') as Container | undefined;

            for (const $marker of [...el.querySelectorAll(':scope > [data-action="add"]')]) {
                $marker.remove();
            }

            const blocks = [...el.children].filter((child): child is HTMLElement => child.tagName === 'ROOTIN-BLOCK');
            const isAtomicKind = (block: HTMLElement | null): boolean =>
                block !== null && block.getAttribute('data-kind') === 'atomic';

            const buildMarker = (isLast: boolean): Dom => {
                const custom = container?.createMarker?.(container, isLast) ?? null;
                const $marker = custom ?? Html.create('button', { type: 'button' });

                $marker.setAttr('data-action', 'add');
                if (isLast === true) {
                    $marker.setAttr('data-last', 'true');
                }
                if (custom === null) {
                    $marker.setAttr('type', 'button');
                    $marker.append(Html.icon('mi mi-plus'));
                } else {
                    $marker.setAttr('data-custom', 'true');
                    Html.get('button', $marker)?.setAttr('contenteditable', 'false');
                }

                $marker.setAttr('contenteditable', 'false');
                $marker.on('mousedown', (_, e) => {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                });

                return $marker;
            };

            if (blocks.length === 0) {
                el.appendChild(buildMarker(true).getEl());
                continue;
            }

            for (let i = 0; i <= blocks.length; i++) {
                const prev = i > 0 ? blocks[i - 1] : null;
                const next = i < blocks.length ? blocks[i] : null;

                const needsMarker =
                    (isAtomicKind(prev) && (next === null || isAtomicKind(next))) ||
                    (isAtomicKind(next) && (prev === null || isAtomicKind(prev)));
                if (needsMarker === false) {
                    continue;
                }

                const $marker = buildMarker(next === null);

                if (next !== null) {
                    el.insertBefore($marker.getEl(), next);
                } else {
                    el.appendChild($marker.getEl());
                }
            }
        }
    }

    /**
     * 최신 히스토리 스냅샷에서 특정 블록고유값을 가진 블록을 소유했던 컨테이너 엘리먼트를 가져온다.
     *
     * @param blockId - 찾을 블록고유값
     * @returns 그 블록을 갖고 있던 컨테이너 엘리먼트
     */
    #findOwningContainer(blockId: string): Container | null {
        for (const [id, snapshot] of this.history.snapshots) {
            if (Array.isArray(snapshot) === false || snapshot.some((item) => item?.id === blockId) === false) {
                continue;
            }

            const $container = Html.get(`[data-element="${id}"]`, this.$getContainer());
            const container = $container?.getData('element') as Container | undefined;
            if (container !== undefined) {
                return container;
            }
        }

        return null;
    }

    /**
     * 현재 포커스된 블록에 따라 블록이 삽입되는 컨테이너 엘리먼트를 가져온다.
     *
     * @returns 블록이 삽입되는 컨테이너 엘리먼트
     */
    #resolveInsertContainer(): Container {
        let container = this.focus.getBlock().getParent() ?? this.root.body;

        while (container !== this.root.body && container.insertTarget === false) {
            container = container.block.getParent() ?? this.root.body;
        }

        return container;
    }

    /**
     * 활성화된 선택영역이 있으면 그 선택영역의 끝(문서 순서상 뒤쪽)을 계산한다.
     *
     * @param container - 블록이 삽입될 컨테이너
     * @returns 삽입 위치(다음 형제 DOM)
     */
    #resolveInsertPosition(container: Container): Node | null {
        const range = this.selection.getRange();
        if (range === null || range.collapsed === true) {
            return null;
        }

        let endBlock: Block | null =
            this.focus.resolveElement(range.endContainer)?.block ?? this.focus.resolveBlock(range.endContainer);
        while (endBlock !== null && endBlock.getParent() !== container) {
            endBlock = endBlock.getParent()?.block ?? null;
        }

        return endBlock?.$getBlock().getEl().nextSibling ?? null;
    }
}
